import React, { useState, useRef } from 'react';
import {
  FileText, Upload, Download, Receipt, DollarSign, Calendar,
  Building, Mail, Phone, Package, Sparkles, Copy, FileSpreadsheet
} from 'lucide-react';
import {
  extractInvoiceData, exportInvoiceAsJSON, exportInvoiceAsCSV
} from '../utils/invoiceExtractionUtils';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import ToolActionButtons from './shared/ToolActionButtons';

const InvoiceExtraction = () => {
  const [file, setFile] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleFileUpload = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      extractData(selectedFile);
    } else {
      toast.error('Please select a file');
    }
  };

  const extractData = async (file) => {
    try {
      setIsProcessing(true);
      setProgress(0);
      const data = await extractInvoiceData(file, setProgress);
      setInvoiceData(data);
      toast.success('Invoice data extracted successfully!');
    } catch (error) {
      toast.error(`Extraction failed: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExportJSON = async () => {
    if (!file) return;

    try {
      setIsProcessing(true);
      setProgress(0);
      const result = await exportInvoiceAsJSON(file, setProgress);
      saveAs(result.blob, result.filename);
      toast.success('Invoice data exported as JSON!');
    } catch (error) {
      toast.error(`Export failed: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExportCSV = async () => {
    if (!file) return;

    try {
      setIsProcessing(true);
      setProgress(0);
      const result = await exportInvoiceAsCSV(file, setProgress);
      saveAs(result.blob, result.filename);
      toast.success('Invoice data exported as CSV!');
    } catch (error) {
      toast.error(`Export failed: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-green-900 to-gray-900 text-white p-6">
      {/* Action Buttons */}
      <ToolActionButtons
        onAdd={() => {
          fileInputRef.current?.click();
        }}
        notificationCount={invoiceData ? 1 : 0}
      />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="tool-header">
          <div className="tool-icon-wrapper">
            <Receipt size={40} color="var(--color-text-primary)" />
          </div>
          <div className="tool-header-content">
            <h1 className="tool-title">Invoice Data Extraction</h1>
            <p className="tool-description">
              AI-powered invoice text and data extraction
            </p>
          </div>
        </div>

        {/* File Upload */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,image/*"
              onChange={handleFileUpload}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="btn-primary flex items-center gap-2"
            >
              <Upload size={20} />
              {file ? 'Change File' : 'Upload Invoice (PDF or Image)'}
            </button>
            {file && (
              <div className="flex items-center gap-2 text-gray-300">
                <FileText size={20} />
                <span>{file.name}</span>
              </div>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        {isProcessing && (
          <div className="mb-6">
            <div className="bg-white/5 rounded-full h-2 overflow-hidden">
              <div
                className="bg-gradient-to-r from-green-500 to-emerald-500 h-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-sm text-gray-400 mt-2">Extracting data... {Math.round(progress)}%</p>
          </div>
        )}

        {/* Invoice Data */}
        {invoiceData && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <FileText className="text-green-400" size={24} />
                  <div className="text-sm text-gray-400">Invoice Number</div>
                </div>
                <div className="text-2xl font-bold">
                  {invoiceData.invoiceNumber || 'N/A'}
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <DollarSign className="text-green-400" size={24} />
                  <div className="text-sm text-gray-400">Total Amount</div>
                </div>
                <div className="text-2xl font-bold">
                  {invoiceData.currency} {invoiceData.total?.toFixed(2) || '0.00'}
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Calendar className="text-green-400" size={24} />
                  <div className="text-sm text-gray-400">Date</div>
                </div>
                <div className="text-2xl font-bold">
                  {invoiceData.date || 'N/A'}
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Package className="text-green-400" size={24} />
                  <div className="text-sm text-gray-400">Items</div>
                </div>
                <div className="text-2xl font-bold">
                  {invoiceData.items?.length || 0}
                </div>
              </div>
            </div>

            {/* Detailed Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Vendor Information */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <Building size={24} />
                  Vendor Information
                </h2>
                <div className="space-y-3">
                  <div>
                    <div className="text-sm text-gray-400">Name</div>
                    <div className="text-lg font-semibold">
                      {invoiceData.vendor?.name || 'N/A'}
                    </div>
                  </div>
                  {invoiceData.vendor?.email && (
                    <div className="flex items-center gap-2">
                      <Mail size={18} className="text-gray-400" />
                      <div>
                        <div className="text-sm text-gray-400">Email</div>
                        <div className="text-lg">{invoiceData.vendor.email}</div>
                      </div>
                    </div>
                  )}
                  {invoiceData.vendor?.phone && (
                    <div className="flex items-center gap-2">
                      <Phone size={18} className="text-gray-400" />
                      <div>
                        <div className="text-sm text-gray-400">Phone</div>
                        <div className="text-lg">{invoiceData.vendor.phone}</div>
                      </div>
                    </div>
                  )}
                  {invoiceData.vendor?.address && (
                    <div>
                      <div className="text-sm text-gray-400">Address</div>
                      <div className="text-lg">{invoiceData.vendor.address}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Financial Summary */}
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <DollarSign size={24} />
                  Financial Summary
                </h2>
                <div className="space-y-3">
                  {invoiceData.subtotal && (
                    <div className="flex justify-between">
                      <div className="text-gray-400">Subtotal</div>
                      <div className="text-lg font-semibold">
                        {invoiceData.currency} {invoiceData.subtotal.toFixed(2)}
                      </div>
                    </div>
                  )}
                  {invoiceData.tax && (
                    <div className="flex justify-between">
                      <div className="text-gray-400">Tax</div>
                      <div className="text-lg font-semibold">
                        {invoiceData.currency} {invoiceData.tax.toFixed(2)}
                      </div>
                    </div>
                  )}
                  {invoiceData.discount && (
                    <div className="flex justify-between">
                      <div className="text-gray-400">Discount</div>
                      <div className="text-lg font-semibold text-red-400">
                        -{invoiceData.currency} {invoiceData.discount.toFixed(2)}
                      </div>
                    </div>
                  )}
                  <div className="flex justify-between pt-3 border-t border-white/10">
                    <div className="text-xl font-bold">Total</div>
                    <div className="text-2xl font-black text-green-400">
                      {invoiceData.currency} {invoiceData.total?.toFixed(2) || '0.00'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Line Items */}
            {invoiceData.items && invoiceData.items.length > 0 && (
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <Package size={24} />
                  Line Items
                </h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-4 text-gray-400">Qty</th>
                        <th className="text-left py-3 px-4 text-gray-400">Description</th>
                        <th className="text-right py-3 px-4 text-gray-400">Unit Price</th>
                        <th className="text-right py-3 px-4 text-gray-400">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceData.items.map((item, index) => (
                        <tr key={index} className="border-b border-white/5">
                          <td className="py-3 px-4">{item.quantity}</td>
                          <td className="py-3 px-4">{item.description}</td>
                          <td className="py-3 px-4 text-right">
                            {invoiceData.currency} {item.unitPrice?.toFixed(2)}
                          </td>
                          <td className="py-3 px-4 text-right font-semibold">
                            {invoiceData.currency} {item.total?.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Export Options */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold">Export Options</h2>
                <div className="flex gap-2">
                  <button
                    onClick={handleExportJSON}
                    className="btn-primary flex items-center gap-2"
                    disabled={isProcessing}
                  >
                    <FileText size={18} />
                    Export JSON
                  </button>
                  <button
                    onClick={handleExportCSV}
                    className="btn-outline flex items-center gap-2"
                    disabled={isProcessing}
                  >
                    <FileSpreadsheet size={18} />
                    Export CSV
                  </button>
                  <button
                    onClick={() => copyToClipboard(JSON.stringify(invoiceData, null, 2))}
                    className="btn-outline flex items-center gap-2"
                  >
                    <Copy size={18} />
                    Copy JSON
                  </button>
                </div>
              </div>
              <pre className="bg-black/30 rounded-lg p-4 overflow-x-auto text-sm max-h-96">
                {JSON.stringify(invoiceData, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {!invoiceData && !isProcessing && (
          <div className="text-center py-12 text-gray-400">
            <Receipt size={64} className="mx-auto mb-4 opacity-50" />
            <p>Upload an invoice (PDF or image) to extract data automatically</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceExtraction;


